import { useState, type ReactNode } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Pencil, RotateCcw, X } from 'lucide-react';

import type { PayrollAdjustmentPatch, PayrollShift, PayrollShiftIssue, PayrollTotals } from '../../types';
import { cn } from '../../utils/cn';
import { formatDateKey, formatMoneyInput, formatVnd, formatWeekday, parseMoneyInput } from '../../utils/money';
import { openOnRowClick } from '../../utils/rowClick';
import { Button, LocalPagination, ResponsiveDataView, StatusBadge, TimeInput, useLocalPagination } from '../ui';
import { ShiftSessionModal } from './ShiftSessionModal';
import { StaffIssueModal } from './StaffIssueModal';

const slotLabels = { TEACHER: 'Giảng dạy', ASSISTANT: 'Trợ giảng' } as const;

type MoneyField = 'amount' | 'penalty' | 'bonus';

const fieldLabels: Record<MoneyField, string> = { amount: 'Tiền ca', penalty: 'Phạt', bonus: 'Thưởng' };

export type AdjustShift = (shift: PayrollShift, patch: PayrollAdjustmentPatch) => Promise<void>;
// Saves the person's actual start/end for a shift (the same times as on the attendance tab).
export type ChangeShiftTimes = (shift: PayrollShift, times: { startTime: string; endTime: string }) => Promise<void>;

function shiftKey(shift: PayrollShift) {
    return `${shift.attendanceId}-${shift.slot}`;
}

function fieldState(shift: PayrollShift, field: MoneyField) {
    if (field === 'amount') return { value: shift.amount, custom: shift.amountCustom, fallback: shift.computedAmount };
    if (field === 'penalty') return { value: shift.penalty, custom: shift.penaltyCustom, fallback: 0 };
    return { value: shift.bonus, custom: shift.bonusCustom, fallback: 0 };
}

// Punctuality against the class schedule: "Trễ 15′", "Về sớm 30′", both, or "Đúng giờ".
function punctuality(shift: PayrollShift): { text: string; onTime: boolean } | null {
    if (!shift.scheduledStart || !shift.scheduledEnd) return null;
    const parts = [];
    if (shift.lateMinutes) parts.push(`Trễ ${shift.lateMinutes}′`);
    if (shift.earlyMinutes) parts.push(`Về sớm ${shift.earlyMinutes}′`);
    return parts.length ? { text: parts.join(' · '), onTime: false } : { text: 'Đúng giờ', onTime: true };
}

function PunctualityText({ shift }: { shift: PayrollShift }) {
    const value = punctuality(shift);
    if (!value) return <span className="text-ink-400">—</span>;
    return <span className={value.onTime ? 'text-success-700' : 'text-warning-700'}>{value.text}</span>;
}

// A money value that falls back to the system's number until an admin sets a custom one with the pencil.
function MoneyCell({ shift, field, editable, onAdjust }: {
    shift: PayrollShift;
    field: MoneyField;
    editable: boolean;
    onAdjust?: AdjustShift;
}) {
    const { value, custom, fallback } = fieldState(shift, field);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const label = `${fieldLabels[field]} ${formatDateKey(shift.date)} ${shift.className}`;

    const save = async (next: number | null) => {
        if (!onAdjust) return;
        setSaving(true);
        try {
            await onAdjust(shift, { [field]: next });
            setEditing(false);
        } catch {
            // The page shows the error toast; keep the input open.
        } finally {
            setSaving(false);
        }
    };

    const open = () => {
        setDraft(formatMoneyInput(value ?? fallback ?? 0));
        setEditing(true);
    };

    if (editing) {
        return (
            <div className="flex items-center gap-1">
                <input
                    inputMode="numeric"
                    aria-label={label}
                    value={draft}
                    disabled={saving}
                    autoFocus
                    onChange={event => setDraft(formatMoneyInput(parseMoneyInput(event.target.value)))}
                    onKeyDown={event => {
                        if (event.key === 'Enter') save(parseMoneyInput(draft) ?? 0);
                        if (event.key === 'Escape') setEditing(false);
                    }}
                    className="w-28 rounded-md border border-academy-300 px-2 py-1 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-academy-200"
                />
                <Button size="icon" variant="ghost" className="h-8 w-8" loading={saving} aria-label={`Lưu ${label}`} icon={<Check className="h-4 w-4" />} onClick={() => save(parseMoneyInput(draft) ?? 0)} />
                {custom ? (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        disabled={saving}
                        aria-label={`Dùng số hệ thống tính cho ${label}`}
                        title={`Dùng số hệ thống tính${fallback !== null ? ` (${formatVnd(fallback)})` : ''}`}
                        icon={<RotateCcw className="h-4 w-4" />}
                        onClick={() => save(null)}
                    />
                ) : null}
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={saving} aria-label={`Huỷ sửa ${label}`} icon={<X className="h-4 w-4" />} onClick={() => setEditing(false)} />
            </div>
        );
    }

    const money = field === 'amount' ? formatVnd(value) : value ? formatVnd(value) : '0 ₫';

    return (
        <div className="flex min-w-0 items-center gap-1">
            <span
                className={cn(
                    'whitespace-nowrap text-sm tabular-nums',
                    custom ? 'font-semibold text-academy-800' : value === null ? 'text-warning-700' : 'text-ink-800',
                )}
                title={custom && fallback !== null ? `Đã chỉnh tay · hệ thống tính: ${formatVnd(fallback)}` : undefined}
            >
                {value === null ? 'Chưa có lương' : money}
            </span>
            {custom ? (
                <span className="whitespace-nowrap text-[10px] font-medium text-academy-600">Tuỳ chỉnh</span>
            ) : null}
            {editable ? (
                <button
                    type="button"
                    aria-label={`Sửa ${label}`}
                    onClick={open}
                    className="rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-academy-700"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
            ) : null}
        </div>
    );
}

// Actual start–end of the shift; defaults to the class hours, same data as the attendance tab.
function TimeCell({ shift, onChangeTimes }: { shift: PayrollShift; onChangeTimes?: ChangeShiftTimes }) {
    const [editing, setEditing] = useState(false);
    const [start, setStart] = useState(shift.actualStart ?? '');
    const [end, setEnd] = useState(shift.actualEnd ?? '');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const label = `${formatDateKey(shift.date)} ${shift.className}`;

    const open = () => {
        setStart(shift.actualStart ?? '');
        setEnd(shift.actualEnd ?? '');
        setError('');
        setEditing(true);
    };

    const save = async () => {
        if (!onChangeTimes) return;
        if (!start || !end) {
            setError('Nhập đủ giờ vào và giờ ra.');
            return;
        }
        if (start >= end) {
            setError('Giờ ra phải sau giờ vào.');
            return;
        }
        if (start === shift.actualStart && end === shift.actualEnd) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onChangeTimes(shift, { startTime: start, endTime: end });
            setEditing(false);
        } catch {
            // The page shows the error toast; keep the inputs open.
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        const inputClass = 'w-16 rounded-md border border-academy-300 px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-academy-200';
        return (
            <div className="space-y-1">
                <div className="flex items-center gap-1">
                    <TimeInput aria-label={`Giờ vào ${label}`} value={start} disabled={saving} autoFocus onChange={setStart} className={inputClass} />
                    <span className="text-ink-400">–</span>
                    <TimeInput aria-label={`Giờ ra ${label}`} value={end} disabled={saving} onChange={setEnd} className={inputClass} />
                    <Button size="icon" variant="ghost" className="h-8 w-8" loading={saving} aria-label="Lưu giờ" icon={<Check className="h-4 w-4" />} onClick={save} />
                    <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Huỷ sửa giờ" icon={<X className="h-4 w-4" />} onClick={() => setEditing(false)} disabled={saving} />
                </div>
                {error ? <p className="text-xs text-danger-600">{error}</p> : null}
            </div>
        );
    }

    const changed = shift.actualStart !== shift.scheduledStart || shift.actualEnd !== shift.scheduledEnd;
    return (
        <div className="flex items-center gap-1">
            <span className={cn('whitespace-nowrap tabular-nums', changed ? 'font-semibold text-academy-800' : 'text-ink-800')}>
                {shift.actualStart && shift.actualEnd ? `${shift.actualStart}–${shift.actualEnd}` : '—'}
            </span>
            {onChangeTimes ? (
                <button
                    type="button"
                    aria-label={`Sửa giờ vào/ra ${label}`}
                    onClick={open}
                    className="rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-academy-700"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
            ) : null}
        </div>
    );
}

// The admin's note; check-in problems are under "Chuyên cần", not here.
function NoteCell({ shift, editable, onAdjust }: { shift: PayrollShift; editable: boolean; onAdjust?: AdjustShift }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(shift.adminNote ?? '');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!onAdjust) return;
        setSaving(true);
        try {
            await onAdjust(shift, { note: draft.trim() });
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="flex min-w-48 items-start gap-1">
                <textarea
                    aria-label={`Ghi chú ${formatDateKey(shift.date)} ${shift.className}`}
                    value={draft}
                    rows={2}
                    autoFocus
                    onChange={event => setDraft(event.target.value)}
                    className="min-w-0 flex-1 rounded-md border border-academy-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-academy-200"
                />
                <Button size="icon" variant="ghost" className="h-8 w-8" loading={saving} aria-label="Lưu ghi chú" icon={<Check className="h-4 w-4" />} onClick={save} />
                <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Huỷ" icon={<X className="h-4 w-4" />} onClick={() => setEditing(false)} />
            </div>
        );
    }

    return (
        <div className="flex min-w-0 items-start gap-1">
            <div className="min-w-0 flex-1 text-xs">
                {shift.adminNote ? <p className="whitespace-pre-line text-ink-700">{shift.adminNote}</p> : null}
            </div>
            {editable ? (
                <button
                    type="button"
                    aria-label={`Sửa ghi chú ${formatDateKey(shift.date)} ${shift.className}`}
                    onClick={() => { setDraft(shift.adminNote ?? ''); setEditing(true); }}
                    className="rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-academy-700"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
            ) : null}
        </div>
    );
}

// Late, early, check-in problems and other events to look at, under the punctuality. With onOpen (admins and
// managers) each one with a notification opens it; the details are there, so only the label is shown here.
export function IssueList({ issues, onOpen }: { issues?: PayrollShiftIssue[]; onOpen?: (issue: PayrollShiftIssue) => void }) {
    if (!issues?.length) return null;
    return (
        <ul className="mt-1 space-y-1">
            {issues.map(issue => {
                const label = (
                    <>
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        {issue.label}
                    </>
                );
                return (
                    <li key={`${issue.kind}-${issue.activityId}`} className="text-xs">
                        {onOpen && issue.activityId ? (
                            <button type="button" title={issue.detail ?? undefined} onClick={() => onOpen(issue)} className="inline-flex items-center gap-1 text-left font-medium text-warning-700 hover:underline">
                                {label}
                            </button>
                        ) : (
                            <span title={issue.detail ?? undefined} className="inline-flex items-center gap-1 font-medium text-warning-700">{label}</span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

// The session on the attendance tab, where the shift comes from.
export function shiftHref(shift: Pick<PayrollShift, 'date' | 'classId' | 'attendanceId'>) {
    const params = new URLSearchParams({ date: shift.date, classId: shift.classId, attendanceId: shift.attendanceId });
    return `/attendance?${params.toString()}`;
}

function emptyTotals(): PayrollTotals {
    return { teachingShifts: 0, assistantShifts: 0, hours: 0, shiftTypes: [], amount: 0, penalty: 0, bonus: 0, total: 0, missingRateShifts: 0 };
}

function addShiftType(totals: PayrollTotals, type: PayrollTotals['shiftTypes'][number]) {
    const existing = totals.shiftTypes.find(item => item.shiftTypeId === type.shiftTypeId && item.slot === type.slot);
    if (existing) existing.count += type.count;
    else totals.shiftTypes.push({ ...type });
}

// Same order as the server: by name, teaching before assisting, classes without a type last.
function sortShiftTypes(totals: PayrollTotals) {
    totals.shiftTypes.sort((a, b) => (a.shiftTypeId === null ? 1 : 0) - (b.shiftTypeId === null ? 1 : 0)
        || a.name.localeCompare(b.name, 'vi')
        || (a.slot === 'TEACHER' ? 0 : 1) - (b.slot === 'TEACHER' ? 0 : 1));
    return totals;
}

// Totals of the listed shifts, for a filtered list.
export function totalsOf(shifts: PayrollShift[]): PayrollTotals {
    const totals = emptyTotals();
    for (const shift of shifts) {
        if (shift.slot === 'TEACHER') totals.teachingShifts += 1;
        else totals.assistantShifts += 1;
        totals.hours += shift.hours;
        addShiftType(totals, { shiftTypeId: shift.shiftTypeId, name: shift.shiftTypeName ?? 'Chưa có loại ca', slot: shift.slot, count: 1 });
        totals.penalty += shift.penalty;
        totals.bonus += shift.bonus;
        if (shift.amount === null) {
            totals.missingRateShifts += 1;
            totals.total += shift.bonus - shift.penalty;
        } else {
            totals.amount += shift.amount;
            totals.total += shift.total ?? 0;
        }
    }
    return sortShiftTypes(totals);
}

// Several people's totals added up, for the summary.
export function sumTotals(list: PayrollTotals[]): PayrollTotals {
    const totals = emptyTotals();
    for (const item of list) {
        totals.teachingShifts += item.teachingShifts;
        totals.assistantShifts += item.assistantShifts;
        totals.hours += item.hours;
        totals.amount += item.amount;
        totals.penalty += item.penalty;
        totals.bonus += item.bonus;
        totals.total += item.total;
        totals.missingRateShifts += item.missingRateShifts;
        for (const type of item.shiftTypes) addShiftType(totals, type);
    }
    return sortShiftTypes(totals);
}

export const SHIFT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

interface PayrollShiftTableProps {
    shifts: PayrollShift[];
    editable?: boolean;
    onAdjust?: AdjustShift;
    onChangeTimes?: ChangeShiftTimes;
    // Opens each issue's notification (admins and managers only).
    linkIssues?: boolean;
}

// One row per shift, a page at a time; clicking a row (outside the edit controls) shows its session in a modal.
// The totals are above the list, in PayrollTotalsGrid.
export function PayrollShiftTable({ shifts, editable = false, onAdjust, onChangeTimes, linkIssues = false }: PayrollShiftTableProps) {
    const [openKey, setOpenKey] = useState<string | null>(null);
    const [openIssue, setOpenIssue] = useState<PayrollShiftIssue | null>(null);
    const { page, pageSize, pagedItems, setPage, setPageSize } = useLocalPagination(shifts, SHIFT_PAGE_SIZE, shifts.map(shiftKey).join(','));
    const openShift = shifts.find(shift => shiftKey(shift) === openKey) ?? null;
    if (shifts.length === 0) {
        return <p className="rounded-lg border border-dashed border-ink-200 p-6 text-center text-sm text-ink-500">Không có ca nào trong khoảng thời gian này.</p>;
    }

    const cellProps = { editable, onAdjust };
    const onOpenIssue = linkIssues ? setOpenIssue : undefined;
    const header = 'px-3 py-2 text-left text-xs font-medium text-ink-500';
    const rowClick = (shift: PayrollShift) => openOnRowClick(() => setOpenKey(shiftKey(shift)));
    const typeLine = (shift: PayrollShift) => (
        `${shift.shiftTypeName ?? 'Lớp chưa có loại ca'} · ${shift.rate === null ? 'chưa có lương' : `${formatVnd(shift.rate)}/ca`}`
    );

    return (
        <div>
            <ResponsiveDataView
                desktopLabel="Danh sách ca dạy"
                mobileLabel="Danh sách ca dạy (di động)"
                desktop={(
                    <table className="min-w-full divide-y divide-ink-200 text-sm">
                        <thead>
                            <tr>
                                <th className={header}>Ngày</th>
                                <th className={header}>Lớp · vai trò</th>
                                <th className={header}>Giờ vào–ra · loại ca</th>
                                <th className={header}>Tiền ca</th>
                                <th className={header}>Phạt</th>
                                <th className={header}>Thưởng</th>
                                <th className={cn(header, 'text-right')}>Thực nhận</th>
                                <th className={header}>Chuyên cần</th>
                                <th className={header}>Ghi chú</th>
                                <th className={header}><span className="sr-only">Xem</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-100 bg-white">
                            {pagedItems.map(shift => (
                                <tr
                                    key={shiftKey(shift)}
                                    aria-label={`Ca ${formatDateKey(shift.date)} ${shift.className}`}
                                    onClick={rowClick(shift)}
                                    className="cursor-pointer align-top hover:bg-ink-100/60"
                                >
                                    <td className="whitespace-nowrap px-3 py-3">
                                        <span className="font-medium text-ink-900">{formatDateKey(shift.date)}</span>
                                        <span className="ml-1 text-xs text-ink-500">{formatWeekday(shift.date)}</span>
                                    </td>
                                    <td className="min-w-36 px-3 py-3">
                                        <p className="font-medium text-ink-900">{shift.className}</p>
                                        <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-ink-500">
                                            <StatusBadge tone={shift.slot === 'TEACHER' ? 'info' : 'neutral'} icon={null}>{slotLabels[shift.slot]}</StatusBadge>
                                            {shift.campusName}
                                        </p>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3">
                                        <TimeCell shift={shift} onChangeTimes={onChangeTimes} />
                                        <p className="text-xs tabular-nums text-ink-500">
                                            {shift.scheduledStart && shift.scheduledEnd ? `Lịch ${shift.scheduledStart}–${shift.scheduledEnd} · ` : ''}
                                            {typeLine(shift)}
                                        </p>
                                    </td>
                                    <td className="px-3 py-3"><MoneyCell shift={shift} field="amount" {...cellProps} /></td>
                                    <td className="px-3 py-3"><MoneyCell shift={shift} field="penalty" {...cellProps} /></td>
                                    <td className="px-3 py-3"><MoneyCell shift={shift} field="bonus" {...cellProps} /></td>
                                    <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-ink-900">{formatVnd(shift.total)}</td>
                                    <td className="min-w-36 px-3 py-3 text-xs">
                                        <PunctualityText shift={shift} />
                                        <IssueList issues={shift.issues} onOpen={onOpenIssue} />
                                    </td>
                                    <td className="min-w-44 max-w-64 px-3 py-3"><NoteCell shift={shift} {...cellProps} /></td>
                                    <td className="px-2 py-3 text-ink-400"><ChevronRight className="h-4 w-4" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                mobile={pagedItems.map(shift => (
                    <article
                        key={shiftKey(shift)}
                        onClick={rowClick(shift)}
                        className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-semibold text-ink-900">{shift.className}</p>
                                <p className="text-xs text-ink-500">
                                    {formatWeekday(shift.date)} {formatDateKey(shift.date)}
                                    {shift.scheduledStart && shift.scheduledEnd ? ` · Lịch ${shift.scheduledStart}–${shift.scheduledEnd}` : ''}
                                    {` · ${typeLine(shift)}`}
                                </p>
                            </div>
                            <StatusBadge tone={shift.slot === 'TEACHER' ? 'info' : 'neutral'} icon={null}>{slotLabels[shift.slot]}</StatusBadge>
                        </div>
                        <dl className="mt-3 space-y-2 text-sm">
                            <div className="flex items-center justify-between gap-3">
                                <dt className="text-ink-500">Giờ vào–ra</dt>
                                <dd><TimeCell shift={shift} onChangeTimes={onChangeTimes} /></dd>
                            </div>
                            {(['amount', 'penalty', 'bonus'] as MoneyField[]).map(field => (
                                <div key={field} className="flex items-center justify-between gap-3">
                                    <dt className="text-ink-500">{fieldLabels[field]}</dt>
                                    <dd><MoneyCell shift={shift} field={field} {...cellProps} /></dd>
                                </div>
                            ))}
                            <div className="flex items-start justify-between gap-3">
                                <dt className="text-ink-500">Chuyên cần</dt>
                                <dd className="text-right">
                                    <PunctualityText shift={shift} />
                                    <IssueList issues={shift.issues} onOpen={onOpenIssue} />
                                </dd>
                            </div>
                            <div>
                                <dt className="text-ink-500">Ghi chú</dt>
                                <dd className="mt-1"><NoteCell shift={shift} {...cellProps} /></dd>
                            </div>
                            <div className="flex items-center justify-between gap-3 border-t border-ink-200 pt-2 font-semibold">
                                <dt>Thực nhận</dt>
                                <dd className="tabular-nums">{formatVnd(shift.total)}</dd>
                            </div>
                        </dl>
                    </article>
                ))}
            />
            <LocalPagination
                label="ca dạy"
                page={page}
                pageSize={pageSize}
                totalItems={shifts.length}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
            />
            <ShiftSessionModal
                shift={openShift}
                href={openShift ? shiftHref(openShift) : ''}
                onOpenIssue={onOpenIssue && (issue => { setOpenKey(null); setOpenIssue(issue); })}
                onClose={() => setOpenKey(null)}
            />
            <StaffIssueModal issue={openIssue} onClose={() => setOpenIssue(null)} />
        </div>
    );
}

// "4 × Ca 120 phút · Trợ giảng", one per line.
export function ShiftTypeLines({ totals, className }: { totals: Pick<PayrollTotals, 'shiftTypes'>; className?: string }) {
    if (!totals.shiftTypes.length) return <span className="text-ink-400">—</span>;
    return (
        <ul className={cn('space-y-0.5', className)}>
            {totals.shiftTypes.map(item => (
                <li key={`${item.shiftTypeId ?? 'none'}-${item.slot}`} className="whitespace-nowrap">
                    <span className="tabular-nums font-medium">{item.count}</span> × {item.name}
                    <span className="text-ink-500"> · {slotLabels[item.slot]}</span>
                </li>
            ))}
        </ul>
    );
}

// One line of shift counts with the money beside it; the per-type table (teaching and assisting as columns) opens on demand.
export function PayrollTotalsGrid({ totals, people }: { totals: PayrollTotals; people?: number }) {
    const [showTypes, setShowTypes] = useState(false);
    const shiftCount = totals.teachingShifts + totals.assistantShifts;
    // One row per shift type, teaching and assisting side by side.
    const typeRows: Array<{ shiftTypeId: string | null; name: string; TEACHER: number; ASSISTANT: number }> = [];
    for (const item of totals.shiftTypes) {
        let row = typeRows.find(entry => entry.shiftTypeId === item.shiftTypeId);
        if (!row) typeRows.push(row = { shiftTypeId: item.shiftTypeId, name: item.name, TEACHER: 0, ASSISTANT: 0 });
        row[item.slot] += item.count;
    }
    const money = [
        { label: 'Tiền ca', value: totals.amount },
        { label: 'Phạt', value: totals.penalty },
        { label: 'Thưởng', value: totals.bonus },
    ];
    return (
        <div className="space-y-2">
            <div aria-label="Tổng chấm công" role="group" className="overflow-hidden rounded-lg border border-ink-200 bg-white">
                <div className="grid gap-px bg-ink-200 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))]">
                    <button
                        type="button"
                        aria-expanded={showTypes}
                        onClick={() => setShowTypes(open => !open)}
                        className="flex items-center justify-between gap-3 bg-white px-4 py-3 text-left hover:bg-ink-50 focus:outline-none focus-visible:bg-academy-50 sm:col-span-2 lg:col-span-1"
                    >
                        <span className="min-w-0">
                            <span className="block text-xs text-ink-500">Số ca{people !== undefined ? ` · ${people} nhân sự` : ''}</span>
                            <span className="mt-0.5 block text-base font-semibold tabular-nums text-ink-900">
                                {shiftCount} ca
                                <span className="ml-2 text-sm font-normal text-ink-600">{totals.teachingShifts} giảng dạy · {totals.assistantShifts} trợ giảng</span>
                            </span>
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-academy-700">
                            Theo loại ca
                            <ChevronDown className={cn('h-4 w-4 transition-transform', showTypes && 'rotate-180')} />
                        </span>
                    </button>
                    {money.map(item => (
                        <div key={item.label} className="bg-white px-4 py-3">
                            <p className="text-xs text-ink-500">{item.label}</p>
                            <p className="mt-0.5 text-base font-semibold tabular-nums text-ink-900">{formatVnd(item.value)}</p>
                        </div>
                    ))}
                    <div className="bg-academy-50 px-4 py-3">
                        <p className="text-xs font-medium text-academy-800">Tổng</p>
                        <p className="mt-0.5 text-base font-semibold tabular-nums text-academy-800">{formatVnd(totals.total)}</p>
                    </div>
                </div>
                {showTypes ? (
                    <div className="overflow-x-auto border-t border-ink-200">
                        <table aria-label="Số ca theo loại" className="min-w-full text-sm tabular-nums">
                            <thead className="bg-ink-50 text-xs text-ink-500">
                                <tr>
                                    <th className="px-4 py-1.5 text-left font-medium">Loại ca</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Giảng dạy</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Trợ giảng</th>
                                    <th className="px-4 py-1.5 text-right font-medium">Tổng</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-ink-100">
                                {typeRows.map(row => (
                                    <tr key={row.shiftTypeId ?? 'none'}>
                                        <td className={cn('px-4 py-1.5', row.shiftTypeId ? 'text-ink-900' : 'text-warning-700')}>{row.name}</td>
                                        <td className="px-3 py-1.5 text-right text-ink-700">{row.TEACHER || '–'}</td>
                                        <td className="px-3 py-1.5 text-right text-ink-700">{row.ASSISTANT || '–'}</td>
                                        <td className="px-4 py-1.5 text-right font-medium">{row.TEACHER + row.ASSISTANT}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </div>
            {totals.missingRateShifts ? (
                <p className="text-sm text-warning-700">
                    {totals.missingRateShifts} ca chưa có mức lương/ca (hoặc lớp chưa có loại ca) nên chưa được tính tiền ca.
                </p>
            ) : null}
        </div>
    );
}
